import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { PerformanceBadge } from '../components/PerformanceBadge';

interface Creative {
  id: string;
  name: string;
  type: 'image' | 'video' | 'carousel';
  platform: 'meta' | 'google' | 'tiktok';
  status: 'active' | 'paused' | 'archived';
  thumbnailUrl: string;
  assetUrl: string;
  campaignName: string;
  campaignId: string;
  createdAt: string;
  updatedAt: string;
  dimensions: string;
  fileSize: string;
  duration?: string;
  impressions: number;
  clicks: number;
  ctr: number;
  conversions: number;
  spend: number;
  roas: number;
  cpc: number;
  cpm: number;
}

interface Variant {
  id: string;
  name: string;
  thumbnailUrl: string;
  impressions: number;
  ctr: number;
  conversions: number;
  isWinner: boolean;
}

interface ABTest {
  id: string;
  name: string;
  status: 'running' | 'completed';
  startDate: string;
  endDate: string | null;
  variants: Variant[];
}

export const CreativeDetailPage: React.FC = () => {
  const { creativeId } = useParams<{ creativeId: string }>();
  const [activeTab, setActiveTab] = useState<'overview' | 'variants' | 'history'>('overview');

  // Mock data
  const creative: Creative = {
    id: creativeId || '1',
    name: 'Summer Sale Hero Image',
    type: 'image',
    platform: 'meta',
    status: 'active',
    thumbnailUrl: 'https://placehold.co/400x400/FFA500/FFFFFF?text=Creative',
    assetUrl: 'https://placehold.co/1200x628/FFA500/FFFFFF?text=Summer+Sale',
    campaignName: 'Summer Sale 2026',
    campaignId: 'camp-001',
    createdAt: '2026-01-15',
    updatedAt: '2026-01-27',
    dimensions: '1200x628px',
    fileSize: '245 KB',
    impressions: 145230,
    clicks: 3654,
    ctr: 2.52,
    conversions: 342,
    spend: 2450,
    roas: 4.2,
    cpc: 0.67,
    cpm: 16.87,
  };

  const abTest: ABTest = {
    id: 'test-1',
    name: 'Summer Sale Image Test',
    status: 'completed',
    startDate: '2026-01-15',
    endDate: '2026-01-25',
    variants: [
      {
        id: 'var-1',
        name: 'Original',
        thumbnailUrl: 'https://placehold.co/200x200/FFA500/FFFFFF?text=A',
        impressions: 72000,
        ctr: 2.52,
        conversions: 180,
        isWinner: true,
      },
      {
        id: 'var-2',
        name: 'Variant B',
        thumbnailUrl: 'https://placehold.co/200x200/4CAF50/FFFFFF?text=B',
        impressions: 73230,
        ctr: 2.21,
        conversions: 162,
        isWinner: false,
      },
    ],
  };

  const aiSuggestions = [
    'Try testing a version with more contrast in the call-to-action button',
    'Consider creating a carousel variant to show multiple products',
    'A/B test with a video version - video ads show 35% higher engagement on Meta',
  ];

  const performanceHistory = [
    { date: '2026-01-27', impressions: 12430, clicks: 312, conversions: 29, spend: 209 },
    { date: '2026-01-26', impressions: 11890, clicks: 298, conversions: 27, spend: 199 },
    { date: '2026-01-25', impressions: 13120, clicks: 331, conversions: 31, spend: 221 },
  ];

  const handleDuplicate = () => {
    console.log('Duplicating creative:', creative.id);
    // TODO: Call API
  };

  const handleArchive = () => {
    if (confirm('Are you sure you want to archive this creative?')) {
      console.log('Archiving creative:', creative.id);
      // TODO: Call API
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{creative.name}</h1>
            <span className={`px-2 py-1 rounded text-sm capitalize ${
              creative.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
              creative.status === 'paused' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
              'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
            }`}>
              {creative.status}
            </span>
            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded text-sm capitalize">
              {creative.type}
            </span>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            Campaign: <a href={`/campaigns/${creative.campaignId}`} className="text-gp-sungold hover:underline">{creative.campaignName}</a>
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleDuplicate}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Duplicate
          </button>
          <button
            onClick={handleArchive}
            className="px-4 py-2 border border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            Archive
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Impressions</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">{creative.impressions.toLocaleString()}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Clicks</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">{creative.clicks.toLocaleString()}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">CTR</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">{creative.ctr}%</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Conversions</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">{creative.conversions}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Spend</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">${creative.spend.toLocaleString()}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">ROAS</p>
          <p className="text-xl font-bold text-green-600 mt-1">{creative.roas}x</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">CPC</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">${creative.cpc}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">CPM</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">${creative.cpm}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-8">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'variants', label: 'A/B Testing' },
            { id: 'history', label: 'Performance History' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-gp-sungold text-gp-sungold'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Preview */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Preview</h3>
              <div className="bg-gray-100 dark:bg-gray-900 rounded-lg p-4 flex items-center justify-center min-h-[300px]">
                <img src={creative.assetUrl} alt={creative.name} className="max-w-full h-auto rounded" />
              </div>
              <div className="mt-4 flex justify-center gap-2">
                <button className="px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
                  Download Asset
                </button>
                <button className="px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
                  View Full Size
                </button>
              </div>
            </div>

            {/* AI Suggestions */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-6">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-300 mb-4">🤖 AI Optimization Suggestions</h3>
              <ul className="space-y-3">
                {aiSuggestions.map((suggestion, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="text-sm text-blue-800 dark:text-blue-300">{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="space-y-6">
            {/* Details */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Platform</span>
                  <span className="font-medium text-gray-900 dark:text-white capitalize">{creative.platform}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Type</span>
                  <span className="font-medium text-gray-900 dark:text-white capitalize">{creative.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Dimensions</span>
                  <span className="font-medium text-gray-900 dark:text-white">{creative.dimensions}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">File Size</span>
                  <span className="font-medium text-gray-900 dark:text-white">{creative.fileSize}</span>
                </div>
                {creative.duration && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Duration</span>
                    <span className="font-medium text-gray-900 dark:text-white">{creative.duration}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Created</span>
                  <span className="font-medium text-gray-900 dark:text-white">{creative.createdAt}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Last Updated</span>
                  <span className="font-medium text-gray-900 dark:text-white">{creative.updatedAt}</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <button className="w-full px-4 py-2 bg-gp-sungold text-white rounded-lg hover:bg-gp-sungold-600">
                  Create A/B Test
                </button>
                <button className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                  Generate Variants
                </button>
                <button className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                  Edit Creative
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Variants Tab */}
      {activeTab === 'variants' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{abTest.name}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {abTest.startDate} - {abTest.endDate || 'Ongoing'} · {abTest.status === 'completed' ? 'Completed' : 'Running'}
              </p>
            </div>
            <button className="px-4 py-2 bg-gp-sungold text-white rounded-lg hover:bg-gp-sungold-600">
              Start New Test
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {abTest.variants.map((variant) => (
              <div key={variant.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 relative">
                {variant.isWinner && (
                  <div className="absolute top-4 right-4">
                    <span className="px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400 rounded text-xs font-medium">
                      Winner
                    </span>
                  </div>
                )}
                <div className="bg-gray-100 dark:bg-gray-900 rounded-lg p-4 mb-4 flex items-center justify-center h-48">
                  <img src={variant.thumbnailUrl} alt={variant.name} className="max-h-full" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">{variant.name}</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600 dark:text-gray-400">Impressions</p>
                    <p className="font-medium text-gray-900 dark:text-white">{variant.impressions.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 dark:text-gray-400">CTR</p>
                    <p className="font-medium text-gray-900 dark:text-white">{variant.ctr}%</p>
                  </div>
                  <div>
                    <p className="text-gray-600 dark:text-gray-400">Conversions</p>
                    <p className="font-medium text-gray-900 dark:text-white">{variant.conversions}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Daily Performance</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Impressions</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Clicks</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Conversions</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Spend</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {performanceHistory.map((day) => (
                  <tr key={day.date} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{day.date}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900 dark:text-white">{day.impressions.toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900 dark:text-white">{day.clicks}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900 dark:text-white">{day.conversions}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900 dark:text-white">${day.spend}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreativeDetailPage;
